import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import { ChevronLeft, BookOpen, Loader2, Download, Library } from 'lucide-react'
import { apiClient } from '../../lib/api'
import type { LibraryBook, LibraryBookTranslation, MembershipTier } from '../../types'
import { useTranslation } from 'react-i18next'
import { DownloadLimitDialog } from '../membership/DownloadLimitDialog'
import { TakeBookSuccessDialog } from './TakeBookSuccessDialog'
import { VersionSelector } from './VersionSelector'
import { StickyActionBar } from './StickyActionBar'
import { useNavigateBack } from '../../hooks/useNavigateBack'

// Error type for download limit errors
interface DownloadLimitError extends Error {
  error_code?: string
  downloads_used?: number
  downloads_limit?: number
}

export function BookDetailPage() {
  const { t } = useTranslation('common')
  const { bookId } = useParams<{ bookId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const goBack = useNavigateBack('/library')
  
  // Book data
  const [book, setBook] = useState<LibraryBook | null>(null)
  const [loading, setLoading] = useState(true)
  
  // Selection state
  const [selectedLanguage, setSelectedLanguage] = useState<string>('')
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null)
  const [selectedTranslation, setSelectedTranslation] = useState<LibraryBookTranslation | null>(null)
  
  // Book taken state (user already has access)
  const [isTaken, setIsTaken] = useState(false)
  const [checkingAccess, setCheckingAccess] = useState(false)
  
  // Action states
  const [isTaking, setIsTaking] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const [downloadingOriginal, setDownloadingOriginal] = useState(false)
  
  // Dialog states
  const [showLimitDialog, setShowLimitDialog] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [limitInfo, setLimitInfo] = useState<{ used: number; limit: number; tier: MembershipTier }>({ 
    used: 0, 
    limit: 0, 
    tier: 'basic' 
  })
  const [takesRemaining, setTakesRemaining] = useState(0)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Load book data
  useEffect(() => {
    loadBook()
  }, [bookId])

  // Auto-select language when book loads
  // Priority: 1) navigation state (from TakenBooksSection), 2) user preference, 3) first available
  useEffect(() => {
    if (book && !selectedLanguage) {
      const availableLangs = Object.keys(book.translations || {})
      const stateLanguage = location.state?.translationLanguage as string | undefined
      
      if (stateLanguage && availableLangs.includes(stateLanguage)) {
        // Use language from navigation state (e.g., from My Books)
        setSelectedLanguage(stateLanguage)
      } else if (user?.preferred_study_language && availableLangs.includes(user.preferred_study_language)) {
        setSelectedLanguage(user.preferred_study_language)
      } else if (availableLangs.length > 0) {
        setSelectedLanguage(availableLangs[0])
      }
    }
  }, [book, user?.preferred_study_language, selectedLanguage, location.state?.translationLanguage])

  // Auto-select level when language changes
  // Only auto-select level when navigating from My Books (with explicit translation state)
  // When coming from library, user must manually select level
  useEffect(() => {
    if (selectedLanguage && book) {
      const translations = book.translations?.[selectedLanguage] || []
      const stateLevel = location.state?.cefrLevel as string | undefined
      const stateLanguage = location.state?.translationLanguage as string | undefined
      const fromMyBooks = location.state?.from === 'my-books'
      
      // Only auto-select level when coming from My Books with matching language
      if (fromMyBooks && stateLevel && stateLanguage === selectedLanguage) {
        const availableLevel = translations.find(
          t => t.status === 'available' && t.cefr_level === stateLevel
        )
        if (availableLevel) {
          setSelectedLevel(stateLevel)
        }
      }
      // When coming from library, don't auto-select any level - user must choose
    }
  }, [selectedLanguage, book, location.state?.cefrLevel, location.state?.translationLanguage, location.state?.from])

  // Update selected translation when level changes
  useEffect(() => {
    if (selectedLanguage && selectedLevel && book) {
      const translations = book.translations?.[selectedLanguage] || []
      const translation = translations.find(
        t => t.cefr_level === selectedLevel && t.status === 'available'
      )
      setSelectedTranslation(translation || null)
    } else {
      setSelectedTranslation(null)
    }
  }, [selectedLanguage, selectedLevel, book])

  // Check if user has already taken this translation
  const checkAccess = useCallback(async () => {
    if (!selectedTranslation || !user) {
      setIsTaken(false)
      return
    }
    
    try {
      setCheckingAccess(true)
      const result = await apiClient.reader.checkAccess(selectedTranslation.id)
      setIsTaken(result.has_read)
    } catch (error) {
      console.error('Failed to check access:', error)
      setIsTaken(false)
    } finally {
      setCheckingAccess(false)
    }
  }, [selectedTranslation, user])

  useEffect(() => {
    checkAccess()
  }, [checkAccess])

  const loadBook = async () => {
    if (!bookId) return
    
    try {
      setLoading(true)
      const data = await apiClient.getLibraryBook(bookId)
      setBook(data.book)
    } catch (error) {
      console.error('Failed to load book:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDownloadError = async (error: DownloadLimitError) => {
    if (error.error_code === 'DOWNLOAD_LIMIT_REACHED') {
      try {
        const membership = await apiClient.membership.getMembership()
        setLimitInfo({
          used: error.downloads_used || 0,
          limit: error.downloads_limit || 0,
          tier: membership.membership?.tier || 'basic'
        })
        setShowLimitDialog(true)
      } catch (e) {
        setLimitInfo({
          used: error.downloads_used || 0,
          limit: error.downloads_limit || 0,
          tier: 'basic'
        })
        setShowLimitDialog(true)
      }
    } else {
      alert(t('book.downloadFailed'))
    }
  }

  const handleTakeBook = async () => {
    if (!bookId || !selectedTranslation || !user) {
      if (!user) {
        navigate('/login', { state: { from: location } })
      }
      return
    }

    try {
      setIsTaking(true)
      
      // Start reading session - this will "take" the book
      const result = await apiClient.reader.startSession(bookId, selectedTranslation.id, false)
      
      if (!result.success) {
        if (result.error_code === 'DOWNLOAD_LIMIT_REACHED') {
          await handleDownloadError({
            message: result.error || 'Download limit reached',
            error_code: result.error_code,
            downloads_used: result.downloads_used,
            downloads_limit: result.downloads_limit
          } as DownloadLimitError)
          return
        }
        throw new Error(result.error || 'Failed to take book')
      }
      
      // Get remaining downloads
      try {
        const limits = await apiClient.membership.getMembershipLimits()
        setTakesRemaining(limits.limits.downloads.remaining)
      } catch {
        setTakesRemaining(0)
      }
      
      setIsTaken(true)
      setShowSuccessDialog(true)
    } catch (error) {
      console.error('Take book failed:', error)
      const err = error as DownloadLimitError
      await handleDownloadError(err)
    } finally {
      setIsTaking(false)
    }
  }

  const handleReadFoliate = () => {
    if (!bookId || !selectedTranslation) return
    // Pass the origin so ReaderPage can navigate back correctly
    navigate(`/read/${bookId}/${selectedTranslation.id}`, { 
      state: { from: location.state?.from } 
    })
  }

  const handleDownload = async () => {
    if (!bookId || !selectedTranslation || !user) {
      if (!user) {
        navigate('/login', { state: { from: location } })
      }
      return
    }

    try {
      setDownloading(true)
      await apiClient.downloadLibraryBook(bookId, selectedTranslation.id)
    } catch (error) {
      const err = error as DownloadLimitError
      if (err.error_code !== 'DOWNLOAD_LIMIT_REACHED') {
        console.error('Download failed:', error)
      }
      await handleDownloadError(err)
    } finally {
      setDownloading(false)
    }
  }

  const handleDownloadOriginal = async () => {
    if (!bookId || !user) {
      if (!user) {
        navigate('/login', { state: { from: location } })
      }
      return
    }

    try {
      setDownloadingOriginal(true)
      await apiClient.downloadLibraryBookOriginal(bookId)
    } catch (error) {
      const err = error as DownloadLimitError
      if (err.error_code !== 'DOWNLOAD_LIMIT_REACHED') {
        console.error('Download original failed:', error)
      }
      await handleDownloadError(err)
    } finally {
      setDownloadingOriginal(false)
    }
  }

  const handlePreview = () => {
    if (!bookId || !selectedTranslation) return
    navigate(`/preview/${bookId}/${selectedTranslation.id}`)
  }

  const handleUpgraded = () => {
    setShowLimitDialog(false)
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const handleLanguageChange = (language: string) => {
    setSelectedLanguage(language)
    setSelectedLevel(null)
    setSelectedTranslation(null)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="text-muted-foreground mt-4">{t('book.loadingBookDetails')}</p>
        </div>
      </div>
    )
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-background">
        <HomeHeader user={user} onLogout={handleLogout} />
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold mb-4">{t('library.bookNotFound')}</h2>
          <Button onClick={() => navigate('/library')}>{t('library.backToLibrary')}</Button>
        </div>
      </div>
    )
  }

  // Build meta info string
  const metaParts: string[] = []
  if (book.original_language) {
    metaParts.push(`${t('book.original', 'Original')}: ${book.original_language.toUpperCase()}`)
  }
  if (book.estimated_pages) {
    metaParts.push(`~${book.estimated_pages} ${t('book.pages', 'pages')}`)
  }
  if (book.genre) {
    metaParts.push(book.genre)
  }
  const metaString = metaParts.join(' · ')

  const fileFormat = selectedTranslation?.file_format.toLowerCase()
  const isEpub = fileFormat === 'epub'
  const isFb2 = fileFormat === 'fb2'
  const canReadOnline = isEpub || isFb2 // Formats supported by FoliateViewer
  // Preview works for EPUB and FB2 translations (original can be in any supported format or none)
  const canPreview = canReadOnline
  const hasSelection = !!selectedTranslation

  return (
    <div className="book-detail-page min-h-screen bg-background pb-24 lg:pb-8">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      {/* Download Limit Dialog */}
      <DownloadLimitDialog
        open={showLimitDialog}
        onClose={() => setShowLimitDialog(false)}
        onUpgraded={handleUpgraded}
        currentTier={limitInfo.tier}
        downloadsUsed={limitInfo.used}
        downloadsLimit={limitInfo.limit}
      />

      {/* Take Book Success Dialog */}
      <TakeBookSuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        onReadNow={handleReadFoliate}
        onDownload={handleDownload}
        onGoToLibrary={() => {
          setShowSuccessDialog(false)
          goBack()
        }}
        bookTitle={book.title_international}
        takesRemaining={takesRemaining}
        isDownloading={downloading}
      />
      
      <div className="container mx-auto px-4 py-4 lg:py-6">
        {/* Back Button */}
        <button
          onClick={goBack}
          className="back-btn flex items-center text-muted-foreground hover:text-foreground mb-4"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          {location.state?.from === 'my-books' 
            ? t('dashboard.backToMyBooks', 'Back to My Books')
            : t('library.backToLibrary')}
        </button>

        {/* ============ MOBILE LAYOUT ============ */}
        <div className="mobile-layout lg:hidden">
          {/* Header: Cover + Title + Meta */}
          <div className="book-header flex gap-4 mb-6">
            {/* Cover - Compact on mobile */}
            <div className="book-cover flex-shrink-0 w-24">
              <div className="aspect-[2/3] bg-muted rounded-lg overflow-hidden">
                {book.cover_url ? (
                  <img
                    src={book.cover_url}
                    alt={book.title_international}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="h-8 w-8 text-muted-foreground" />
                  </div>
                )}
              </div>
            </div>

            {/* Title + Meta */}
            <div className="book-info flex-1 min-w-0 flex items-start gap-2">
              <div className="w-1 h-6 bg-primary rounded-full flex-shrink-0 mt-0.5"></div>
              <div className="min-w-0 space-y-1.5">
                <div className="title-author">
                  <h1 className="text-xl font-bold text-foreground line-clamp-2">
                    {book.title_international}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {t('book.by', 'by')} {book.author_international}
                  </p>
                </div>

                {/* Meta row */}
                {metaString && (
                  <p className="meta-info text-xs text-muted-foreground">
                    {metaString}
                  </p>
                )}

                {/* Badge */}
                <Badge variant="secondary" className="library-badge">
                  <Library className="h-3 w-3 mr-1" />
                  {t('library.publicLibrary', 'Public Library')}
                </Badge>
              </div>
            </div>
          </div>

          {/* Version Selector Card */}
          <div className="version-card bg-card rounded-lg border p-4 mb-4">
            <VersionSelector
              translations={book.translations || {}}
              availableLanguages={book.available_languages}
              selectedLanguage={selectedLanguage}
              selectedLevel={selectedLevel}
              onLanguageChange={handleLanguageChange}
              onLevelChange={setSelectedLevel}
              userPreferredLanguage={user?.preferred_study_language}
            />
            
            {/* Access status indicator - only show when already taken */}
            {isTaken && selectedTranslation && !checkingAccess && (
              <div className="access-granted flex items-center gap-2 mt-3 text-sm text-emerald-600 dark:text-emerald-400">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                {t('library.alreadyTaken', 'Book already in your library')}
              </div>
            )}
          </div>

          {/* Description */}
          {book.description && (
            <div className="description-section bg-card rounded-lg border p-4 mb-4">
              <h3 className="text-sm font-medium text-foreground mb-2">{t('common.description')}</h3>
              <p className="text-sm text-muted-foreground">{book.description}</p>
            </div>
          )}

          {/* Download Original - Small link */}
          {user && book.original_file_path && (
            <div className="download-original text-center">
              <button
                onClick={handleDownloadOriginal}
                disabled={downloadingOriginal}
                className="text-xs text-muted-foreground hover:text-foreground underline"
              >
                {downloadingOriginal ? (
                  <>
                    <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                    {t('library.downloading')}
                  </>
                ) : (
                  <>
                    <Download className="inline h-3 w-3 mr-1" />
                    {t('book.downloadOriginal')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>

        {/* ============ DESKTOP LAYOUT - Two Columns ============ */}
        <div className="desktop-layout hidden lg:block">
          <div className="grid grid-cols-3 gap-6">
            {/* Left Column - Cover + Details */}
            <div className="col-span-1 space-y-6">
              {/* Cover - Full size on desktop */}
              <div className="book-cover-desktop">
                <div className="aspect-[2/3] bg-muted rounded-lg overflow-hidden max-w-[400px]">
                  {book.cover_url ? (
                    <img
                      src={book.cover_url}
                      alt={book.title_international}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="h-12 w-12 text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>

              {/* Book Details */}
              <div className="book-details space-y-3">
                {book.genre && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('book.genre')}</h3>
                    <p className="text-foreground">{book.genre}</p>
                  </div>
                )}
                {book.estimated_pages && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('book.estimatedPages')}</h3>
                    <p className="text-foreground">~{book.estimated_pages} {t('book.pages')}</p>
                  </div>
                )}
                {book.original_language && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground mb-1">{t('book.originalLanguage')}</h3>
                    <p className="text-foreground uppercase">{book.original_language}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Title + Version Selector + Actions + Description */}
            <div className="col-span-2 space-y-6">
              {/* Title and Author */}
              <div className="title-section">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-8 bg-primary rounded-full"></div>
                  <div>
                    <h1 className="text-2xl font-bold text-foreground">
                      {book.title_international}
                    </h1>
                    <p className="text-sm text-muted-foreground">
                      {t('book.by', 'by')} {book.author_international}
                    </p>
                  </div>
                </div>
              </div>

              {/* Badge */}
              <Badge variant="secondary" className="library-badge">
                <Library className="h-3 w-3 mr-1" />
                {t('library.publicLibrary', 'Public Library')}
              </Badge>

              {/* Version Selector Card */}
              <div className="version-card bg-card rounded-lg border p-6">
                <VersionSelector
                  translations={book.translations || {}}
                  availableLanguages={book.available_languages}
                  selectedLanguage={selectedLanguage}
                  selectedLevel={selectedLevel}
                  onLanguageChange={handleLanguageChange}
                  onLevelChange={setSelectedLevel}
                  userPreferredLanguage={user?.preferred_study_language}
                />
                
                {/* Access status indicator - only show when already taken */}
                {isTaken && selectedTranslation && !checkingAccess && (
                  <div className="access-granted flex items-center gap-2 mt-3 text-sm text-emerald-600 dark:text-emerald-400">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                    {t('library.alreadyTaken', 'Book already in your library')}
                  </div>
                )}
              </div>

              {/* Desktop Action Buttons (inline, not sticky) */}
              <div className="desktop-actions">
                {user ? (
                  hasSelection ? (
                    <div className="flex flex-wrap gap-3">
                      {!isTaken ? (
                        // NOT TAKEN STATE
                        <>
                          <Button
                            onClick={handleTakeBook}
                            disabled={isTaking || checkingAccess}
                            className="take-book-btn"
                            size="lg"
                          >
                            {isTaking ? (
                              <>
                                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                                {t('library.taking', 'Taking...')}
                              </>
                            ) : (
                              <>
                                <BookOpen className="mr-2 h-5 w-5" />
                                {t('library.takeBook', 'Take Book')}
                              </>
                            )}
                          </Button>
                          {canPreview && (
                            <Button
                              onClick={handlePreview}
                              variant="outline"
                              className="preview-btn"
                              size="lg"
                              disabled={checkingAccess}
                            >
                              {t('reader.preview', 'Preview')}
                            </Button>
                          )}
                        </>
                      ) : (
                        // TAKEN STATE
                        <>
                          {canReadOnline && (
                            <Button
                              onClick={handleReadFoliate}
                              className="read-btn"
                              size="lg"
                              disabled={checkingAccess}
                            >
                              <BookOpen className="mr-2 h-5 w-5" />
                              {t('reader.readOnline', 'Read')}
                            </Button>
                          )}
                          <Button
                            onClick={handleDownload}
                            disabled={downloading || checkingAccess}
                            variant="outline"
                            className="download-btn"
                            size="icon"
                            title={t('common.download')}
                          >
                            {downloading ? (
                              <Loader2 className="h-5 w-5 animate-spin" />
                            ) : (
                              <Download className="h-5 w-5" />
                            )}
                          </Button>
                          {canPreview && (
                            <Button
                              onClick={handlePreview}
                              variant="ghost"
                              className="preview-btn"
                              disabled={checkingAccess}
                            >
                              {t('reader.preview', 'Preview')}
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      {t('library.selectLanguageAndLevel')}
                    </p>
                  )
                ) : (
                  <div className="text-center p-4 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground mb-3">
                      {t('library.signInToDownload')}
                    </p>
                    <Button onClick={() => navigate('/login', { state: { from: location } })}>
                      {t('library.signIn')}
                    </Button>
                  </div>
                )}
                
                {/* Download Original - Small link */}
                {user && book.original_file_path && (
                  <div className="download-original mt-4">
                    <button
                      onClick={handleDownloadOriginal}
                      disabled={downloadingOriginal}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                    >
                      {downloadingOriginal ? (
                        <>
                          <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
                          {t('library.downloading')}
                        </>
                      ) : (
                        <>
                          <Download className="inline h-3 w-3 mr-1" />
                          {t('book.downloadOriginal')}
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>

              {/* Description */}
              {book.description && (
                <div className="description-section">
                  <h3 className="text-lg font-semibold text-foreground mb-2">{t('common.description')}</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">{book.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Action Bar - Mobile Only */}
      <div className="lg:hidden">
        {user ? (
          <StickyActionBar
            isTaken={isTaken}
            canReadOnline={canReadOnline}
            onTakeBook={handleTakeBook}
            onRead={handleReadFoliate}
            onDownload={handleDownload}
            onPreview={handlePreview}
            isTaking={isTaking}
            isDownloading={downloading}
            isCheckingAccess={checkingAccess}
            disabled={!hasSelection}
            disabledReason={t('library.selectLanguageAndLevel')}
            showPreview={canPreview}
          />
        ) : (
          <div className="login-prompt fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t p-4 z-50">
            <div className="max-w-screen-xl mx-auto text-center">
              <p className="text-sm text-muted-foreground mb-3">
                {t('library.signInToDownload')}
              </p>
              <Button onClick={() => navigate('/login', { state: { from: location } })}>
                {t('library.signIn')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
